import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing persistent user selections in localStorage
 * with fallback to defaults when selections are no longer available
 */
export const usePersistedSelections = () => {
    // Storage keys
    const STORAGE_KEYS = {
        SELECTED_HUB_ID: 'selectedHubId',
        SELECTED_HUB_REGION: 'selectedHubRegion',
        SELECTED_PROJECT_ID: 'selectedProjectId',
        SELECTED_CATEGORY_ID: 'selectedCategoryId',
        ACCORDION_STATES: 'accordionStates'
    };

    // Default values
    const DEFAULTS = {
        selectedHubId: '',
        selectedHubRegion: '',
        selectedProjectId: '',
        selectedCategoryId: '1'
    };

    // Initialize state from localStorage or defaults
    const [selections, setSelections] = useState(() => {
        const stored = {};
        Object.keys(DEFAULTS).forEach(key => {
            const storageKey = STORAGE_KEYS[key.toUpperCase()] || key;
            const storedValue = localStorage.getItem(storageKey);
            
            // Handle null, undefined, and "null" string cases
            if (storedValue === null || storedValue === 'null' || storedValue === 'undefined') {
                stored[key] = DEFAULTS[key];
            } else {
                stored[key] = storedValue;
            }
        });
        return stored;
    });

    // Save to localStorage whenever selections change
    useEffect(() => {
        Object.keys(selections).forEach(key => {
            const storageKey = STORAGE_KEYS[key.toUpperCase()] || key;
            const value = selections[key];
            
            // Only store non-empty values, remove empty ones
            if (value && value !== '' && value !== 'null' && value !== 'undefined') {
                localStorage.setItem(storageKey, value);
            } else {
                localStorage.removeItem(storageKey);
            }
        });
    }, [selections]);

    // Generic setter function
    const setSelection = useCallback((key, value) => {
        setSelections(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    // Specific setter functions
    const setSelectedHubId = useCallback((value) => {
        setSelection('selectedHubId', value);
        // Note: Let components handle their own dependency clearing
        // to avoid race conditions
    }, [setSelection]);

    const setSelectedHubRegion = useCallback((value) => {
        setSelection('selectedHubRegion', value);
    }, [setSelection]);

    const setSelectedProjectId = useCallback((value) => {
        setSelection('selectedProjectId', value);
    }, [setSelection]);

    const setSelectedCategoryId = useCallback((value) => {
        setSelection('selectedCategoryId', value);
    }, [setSelection]);

    // Validation functions
    const validateHubSelection = useCallback((availableHubs) => {
        if (!availableHubs || availableHubs.length === 0) return;
        
        const currentHubId = selections.selectedHubId;
        const hubExists = availableHubs.some(hub => hub.id === currentHubId);
        
        if (currentHubId && !hubExists) {
            console.log(`Selected hub ${currentHubId} no longer available, clearing selection`);
            setSelectedHubId('');
        }
    }, [selections.selectedHubId, setSelectedHubId]);

    const validateProjectSelection = useCallback((availableProjects) => {
        // Don't validate if no projects available or no project selected
        if (!availableProjects || availableProjects.length === 0 || !selections.selectedProjectId) {
            return;
        }
        
        const currentProjectId = selections.selectedProjectId;
        const projectExists = availableProjects.some(project => project.id === currentProjectId);
        
        // Only clear if we have projects loaded AND the current selection doesn't exist
        if (!projectExists) {
            console.log(`Selected project ${currentProjectId} no longer available, clearing selection`);
            setSelectedProjectId('');
        }
    }, [selections.selectedProjectId, setSelectedProjectId]);

    // Clear all selections (useful for logout)
    const clearAllSelections = useCallback(() => {
        Object.keys(DEFAULTS).forEach(key => {
            const storageKey = STORAGE_KEYS[key.toUpperCase()] || key;
            localStorage.removeItem(storageKey);
        });
        setSelections(DEFAULTS);
    }, []);

    // Get current selections
    const getCurrentSelections = useCallback(() => selections, [selections]);

    return {
        // Current selections
        selectedHubId: selections.selectedHubId,
        selectedHubRegion: selections.selectedHubRegion,
        selectedProjectId: selections.selectedProjectId,
        selectedCategoryId: selections.selectedCategoryId,
        
        // Setters
        setSelectedHubId,
        setSelectedHubRegion,
        setSelectedProjectId,
        setSelectedCategoryId,
        
        // Validation functions
        validateHubSelection,
        validateProjectSelection,
        
        // Utility functions
        clearAllSelections,
        getCurrentSelections
    };
};

/**
 * Hook for managing accordion states persistence
 */
export const usePersistedAccordionStates = () => {
    const ACCORDION_KEYS = {
        VIEWER: 'viewerAccordionOpen',
        PROJECTS: 'projectsAccordionOpen',
        EXPLORER: 'explorerAccordionOpen',
        ASSETS: 'assetsAccordionOpen'
    };

    const ACCORDION_DEFAULTS = {
        viewerAccordionOpen: false,
        projectsAccordionOpen: false,
        explorerAccordionOpen: false,
        assetsAccordionOpen: false
    };

    // Initialize accordion states from localStorage
    const [accordionStates, setAccordionStates] = useState(() => {
        const stored = {};
        Object.keys(ACCORDION_DEFAULTS).forEach(key => {
            const storedValue = localStorage.getItem(key);
            stored[key] = storedValue !== null ? JSON.parse(storedValue) : ACCORDION_DEFAULTS[key];
        });
        return stored;
    });

    // Save accordion states to localStorage
    useEffect(() => {
        Object.keys(accordionStates).forEach(key => {
            localStorage.setItem(key, JSON.stringify(accordionStates[key]));
        });
    }, [accordionStates]);

    // Generic accordion state setter
    const setAccordionState = useCallback((key, value) => {
        setAccordionStates(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    return {
        // Current states
        isViewerAccordionOpen: accordionStates.viewerAccordionOpen,
        isProjectsAccordionOpen: accordionStates.projectsAccordionOpen,
        isExplorerAccordionOpen: accordionStates.explorerAccordionOpen,
        isAssetsAccordionOpen: accordionStates.assetsAccordionOpen,
        
        // Setters
        setViewerAccordionOpen: (value) => setAccordionState('viewerAccordionOpen', value),
        setProjectsAccordionOpen: (value) => setAccordionState('projectsAccordionOpen', value),
        setExplorerAccordionOpen: (value) => setAccordionState('explorerAccordionOpen', value),
        setAssetsAccordionOpen: (value) => setAccordionState('assetsAccordionOpen', value)
    };
};